// Copyright ©️ 2026 eVolve MEP, LLC

import type { GetPresignedUrlResponse, MarkupPage } from '@evolvemep/api-ts';

import { downloadBlobViaLinkElement } from 'components/Mantine/FileUploads/downloadFileViaLinkElement';

/**
 * Downloads an image file (JPEG or PNG) from S3, overlays any markup,
 * and triggers a download of the result at the original resolution and file type.
 */
export const downloadImageResponseWithMarkup = async (response: GetPresignedUrlResponse, markupPage: MarkupPage) => {
  const preSignedUrl = response.preSignedUrl;
  if (!preSignedUrl) return;

  // Get the source image.
  const imageBlob = await fetch(preSignedUrl).then((res) => res.blob());
  const imageUrl = URL.createObjectURL(imageBlob);

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = imageUrl;
  });

  const { width: imgWidth, height: imgHeight } = img;

  // Draw the image onto a canvas.
  const canvas = new OffscreenCanvas(imgWidth, imgHeight);
  const ctx = canvas.getContext('2d');
  ctx?.drawImage(img, 0, 0);
  URL.revokeObjectURL(imageUrl);
  img.remove();

  // Overlay the markup page.
  const parser = new DOMParser();
  const svgDocument = parser.parseFromString(markupPage.markup, 'image/svg+xml');
  const svgElement = svgDocument.documentElement;

  // Extract document-position metadata stored by MarkupModeExtension.
  const docMetadataElement = svgElement.querySelector('metadata markup_document');
  const leftOffsetInInches = Number(docMetadataElement?.getAttribute('data-document-offset-left-in-inches') ?? '0');
  const topOffsetInInches = Number(docMetadataElement?.getAttribute('data-document-offset-top-in-inches') ?? '0');
  const docWidthInInches = Number(docMetadataElement?.getAttribute('data-document-width-in-inches') ?? '0');
  const docHeightInInches = Number(docMetadataElement?.getAttribute('data-document-height-in-inches') ?? '0');

  // Derive the pixel-per-inch ratio from the original SVG canvas dimensions.
  const svgCanvasWidthInPx = Number(svgElement.getAttribute('width') ?? '0');
  const svgCanvasHeightInPx = Number(svgElement.getAttribute('height') ?? '0');
  const viewBoxDimensions = (svgElement.getAttribute('viewBox') ?? '').split(' ').map(Number);
  const vbMinXInInches = viewBoxDimensions[0] ?? 0;
  const vbMinYInInches = viewBoxDimensions[1] ?? 0;
  const vbWidthInInches = viewBoxDimensions[2] ?? 1;

  const pixelsPerInch = svgCanvasWidthInPx / vbWidthInInches;
  const svgCanvasHeightInInches = svgCanvasHeightInPx / pixelsPerInch;

  // Recalculate the viewBox so it covers the document area.
  const updatedVbMinX = vbMinXInInches + leftOffsetInInches;
  const updatedVbMinY =
    (vbMinYInInches + docHeightInInches + (svgCanvasHeightInInches - (topOffsetInInches + docHeightInInches))) * -1;

  // Size the SVG to match the image's original dimensions.
  svgElement.setAttribute('width', String(imgWidth));
  svgElement.setAttribute('height', String(imgHeight));
  svgElement.setAttribute('viewBox', `${updatedVbMinX} ${updatedVbMinY} ${docWidthInInches} ${docHeightInInches}`);
  svgElement.removeAttribute('style');

  // Wrap all children in a scale(1,-1) group to correct Autodesk's flipped y-axis.
  const flipGroup = svgDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
  flipGroup.setAttribute('transform', 'scale(1,-1)');
  [...svgElement.childNodes].forEach((childNode) => flipGroup.appendChild(childNode));
  svgElement.appendChild(flipGroup);

  // Render the SVG into an image and draw it on top of the canvas.
  const svgBlob = new Blob([new XMLSerializer().serializeToString(svgElement)], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);
  const svgImg = new Image();
  await new Promise<void>((resolve, reject) => {
    svgImg.onload = () => resolve();
    svgImg.onerror = reject;
    svgImg.src = svgUrl;
  });
  ctx?.drawImage(svgImg, 0, 0, imgWidth, imgHeight);
  URL.revokeObjectURL(svgUrl);

  // Export the canvas with the original file type and trigger download.
  const outputBlob = await canvas.convertToBlob({ type: response.contentType, quality: 1 });
  downloadBlobViaLinkElement(response.fileName, outputBlob);
};
